import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Check, Minus } from 'lucide-react';
import DataTable from '../DataTable';
import Modal from '../Modal';
import ConfirmDialog from '../ConfirmDialog';
import IconButton from '../common/IconButton';
import SmartSearchBar from '../leads/SmartSearchBar';
import {
  DiseaseCategoryDto,
  CreateDiseaseCategoryDto,
  UpdateDiseaseCategoryDto,
  diseaseCategoriesApi,
  handleApiError
} from '../../services/settingsService';

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const Textarea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

interface FormData {
  name: string;
  description: string;
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  description?: string;
}

const DiseaseCategoriesTab: React.FC = () => {
  const [categories, setCategories] = useState<DiseaseCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DiseaseCategoryDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Confirm dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    category: DiseaseCategoryDto | null;
    loading: boolean;
  }>({ isOpen: false, category: null, loading: false });

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await diseaseCategoriesApi.getAll();
      setCategories(data);
    } catch (error) {
      console.error('שגיאה בטעינת קטגוריות:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;
    const search = searchTerm.toLowerCase();
    return categories.filter(cat =>
      cat.name.toLowerCase().includes(search) ||
      cat.description?.toLowerCase().includes(search)
    );
  }, [categories, searchTerm]);

  const openModal = (category?: DiseaseCategoryDto) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        isActive: category.isActive
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        isActive: true
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'שם הקטגוריה הוא שדה חובה';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      if (editingCategory) {
        const updateData: UpdateDiseaseCategoryDto = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          isActive: formData.isActive
        };
        await diseaseCategoriesApi.update(editingCategory.id, updateData);
      } else {
        const createData: CreateDiseaseCategoryDto = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          isActive: formData.isActive
        };
        await diseaseCategoriesApi.create(createData);
      }

      closeModal();
      await loadCategories();
    } catch (error) {
      const errorMessage = handleApiError(error);
      setFormErrors({ name: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (category: DiseaseCategoryDto) => {
    setConfirmDialog({
      isOpen: true,
      category,
      loading: false
    });
  };

  const confirmDelete = async () => {
    if (!confirmDialog.category) return;

    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      await diseaseCategoriesApi.delete(confirmDialog.category.id);
      setConfirmDialog({ isOpen: false, category: null, loading: false });
      await loadCategories();
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('שגיאה במחיקת קטגוריה:', errorMessage);
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const columns = [
    {
      key: 'name',
      title: 'שם הקטגוריה',
      width: '200px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'description',
      title: 'תיאור',
      width: '300px',
      render: (value: string) => (
        <CenteredCell>
          <SecondaryText>{value || '-'}</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'diseasesCount',
      title: 'מספר מחלות',
      width: '120px',
      render: (value: number) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '100px',
      render: (value: boolean) => (
        <CenteredCell>
          {value ? (
            <Check size={14} color="#16a34a" strokeWidth={2.5} />
          ) : (
            <Minus size={14} color="#d1d5db" />
          )}
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '100px',
      render: (_: any, record: DiseaseCategoryDto) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={() => openModal(record)}
            title="עריכה"
          />
          <IconButton
            icon={Trash2}
            onClick={() => handleDelete(record)}
            title="מחיקה"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <>
      <TabHeader>
        <SearchWrapper>
          <SmartSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="חיפוש קטגוריות..."
          />
        </SearchWrapper>
        <Button variant="primary" onClick={() => openModal()}>
          <Plus size={16} />
          קטגוריה חדשה
        </Button>
      </TabHeader>

      <DataTable
        data={filteredCategories}
        columns={columns}
        loading={loading}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingCategory ? 'עריכת קטגוריה' : 'הוספת קטגוריה חדשה'}
        footer={
          <>
            <Button onClick={closeModal} disabled={isSubmitting}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'שומר...' : 'שמירה'}
            </Button>
          </>
        }
      >
        <FormContainer>
          <FormGroup>
            <Label>שם הקטגוריה *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              hasError={!!formErrors.name}
              placeholder="הזן שם קטגוריה"
            />
            {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              hasError={!!formErrors.description}
              placeholder="הזן תיאור (אופציונלי)"
            />
            {formErrors.description && <ErrorMessage>{formErrors.description}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              <Label htmlFor="isActive">פעיל</Label>
            </CheckboxContainer>
          </FormGroup>
        </FormContainer>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, category: null, loading: false })}
        onConfirm={confirmDelete}
        title="מחיקת קטגוריה"
        message={`האם אתה בטוח שברצונך למחוק את הקטגוריה "${confirmDialog.category?.name}"?`}
        type="danger"
        loading={confirmDialog.loading}
      />
    </>
  );
};

export default DiseaseCategoriesTab;
